import { forwardRef } from 'react'
import { accents } from 'sutra/tokens/colors'

export interface RadioListOptionProps {
  label: string
  prefix?: string
  subtitle?: string
  isSelected: boolean
  isHighlighted?: boolean
  isDisabled?: boolean
  disabledReason?: string
  onClick: () => void
}

export const RadioListOption = forwardRef<HTMLDivElement, RadioListOptionProps>(
  (
    {
      label,
      prefix,
      subtitle,
      isSelected,
      isHighlighted = false,
      isDisabled = false,
      disabledReason,
      onClick,
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        onClick={onClick}
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '8px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          borderRadius: '6px',
          backgroundColor: isDisabled
            ? '#f3f4f6'
            : isHighlighted
              ? accents.reflectionBlue[100]
              : isSelected
                ? accents.reflectionBlue[50]
                : 'transparent',
          border: isDisabled
            ? '1px solid transparent'
            : isSelected
              ? `1px solid ${accents.reflectionBlue[500]}`
              : '1px solid transparent',
          marginBottom: '4px',
          transition: 'all 0.2s ease',
          opacity: isDisabled ? 0.6 : 1,
        }}
        onMouseEnter={(e) => {
          if (!isSelected && !isDisabled && !isHighlighted) {
            e.currentTarget.style.backgroundColor = accents.reflectionBlue[100]
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected && !isDisabled && !isHighlighted) {
            e.currentTarget.style.backgroundColor = 'transparent'
          }
        }}
      >
        {/* Main option row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Radio button indicator */}
          <div
            style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              border: '2px solid',
              borderColor: isDisabled
                ? '#d1d5db'
                : isSelected
                  ? accents.reflectionBlue[500]
                  : '#d1d5db',
              backgroundColor: isDisabled
                ? 'transparent'
                : isSelected
                  ? accents.reflectionBlue[500]
                  : 'transparent',
              marginRight: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isSelected && !isDisabled && (
              <div
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                }}
              />
            )}
          </div>

          {/* Option label */}
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: '13px',
                fontWeight: isDisabled ? '400' : isSelected ? '600' : '400',
                color: isDisabled
                  ? '#9ca3af'
                  : isSelected
                    ? '#1f2937'
                    : '#374151',
              }}
            >
              {prefix && (
                <span style={{ color: '#9ca3af', fontWeight: '400' }}>
                  {prefix} {'> '}
                </span>
              )}
              {label}
            </div>
            {/* Subtitle annotation */}
            {subtitle && (
              <div
                style={{
                  fontSize: '11px',
                  color: '#9ca3af',
                  marginTop: '2px',
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
        </div>

        {/* Disabled reason text */}
        {isDisabled && disabledReason && (
          <div
            style={{
              marginTop: '4px',
              fontSize: '11px',
              color: '#9ca3af',
              fontStyle: 'italic',
            }}
          >
            {disabledReason}
          </div>
        )}
      </div>
    )
  },
)

RadioListOption.displayName = 'RadioListOption'
